// Import logger from separate module to prevent circular dependencies
export { Log } from './logger.js';

/**
 * Content processing utilities for cocktail data
 * Consolidates duplicate line break processing across components
 */
export const ContentProcessor = {
  
  /**
   * Process ingredients string into HTML list items
   * @param {string} ingredients - Raw ingredients string with line breaks
   * @param {function} escapeHtml - HTML escape function
   * @returns {string} HTML list items
   */
  processIngredients(ingredients, escapeHtml = null) {
    if (!ingredients || typeof ingredients !== 'string') {
      return '';
    }
    
    const ingredientItems = ingredients
      .split(/\r?\n/) // Split by line breaks
      .map(ingredient => ingredient.trim())
      .filter(ingredient => ingredient.length > 0);
    
    return ingredientItems
      .map(ingredient => {
        const escaped = escapeHtml ? escapeHtml(ingredient) : ingredient;
        return `<li>${escaped}</li>`;
      })
      .join('');
  },
  
  /**
   * Process instructions string with line breaks to br tags
   * @param {string} instructions - Raw instructions string with line breaks
   * @param {function} escapeHtml - HTML escape function
   * @returns {string} HTML with br tags for line breaks
   */
  processInstructions(instructions, escapeHtml = null) {
    if (!instructions || typeof instructions !== 'string') {
      return '';
    }
    
    const escaped = escapeHtml ? escapeHtml(instructions) : instructions;
    return escaped.replace(/\r?\n/g, '<br>');
  },
  
  /**
   * Generic line break to list processor
   * @param {string} content - Raw content string with line breaks
   * @param {function} escapeHtml - HTML escape function
   * @returns {string} HTML list items
   */
  processLineBreaksToList(content, escapeHtml = null) {
    if (!content || typeof content !== 'string') {
      return '';
    }
    
    const items = content
      .split(/\r?\n/)
      .map(item => item.trim())
      .filter(item => item.length > 0);
    
    return items
      .map(item => {
        const escaped = escapeHtml ? escapeHtml(item) : item;
        return `<li>${escaped}</li>`;
      })
      .join('');
  },
  
  /**
   * Simple HTML escape function for use with content processing
   * @param {string} text - Text to escape
   * @returns {string} HTML-escaped text
   */
  escapeHtml(text) {
    if (typeof text !== 'string') return text;
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
};

/**
 * Centralized async error handling utility
 */
export class AsyncErrorHandler {
  /**
   * Handle async operations with consistent error reporting
   * @param {Promise} asyncOperation - The async operation to handle
   * @param {Object} context - Context information for error reporting
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  static async handle(asyncOperation, context = {}) {
    try {
      const result = await asyncOperation;
      
      if (context.onSuccess) {
        context.onSuccess(result);
      }
      
      return { success: true, result };
    } catch (error) {
      const log = Log(context.component || 'AsyncErrorHandler');
      log.red(`${context.operation || 'Operation'} failed:`, error);
      
      // Show user feedback if callback provided
      if (context.onError) {
        context.onError(error);
      }
      
      // Report to global error boundary
      window.dispatchEvent(new CustomEvent('component-error', {
        detail: { 
          error, 
          component: context.component,
          context: context.operation,
          ...context.extra
        }
      }));
      
      return { success: false, error };
    }
  }
  
  /**
   * Specialized handler for clipboard operations
   */
  static async clipboard(text, component, successMessage = 'Copied to clipboard!') {
    return this.handle(
      navigator.clipboard.writeText(text),
      {
        component,
        operation: 'clipboard',
        onSuccess: () => {
          component.showToast?.(successMessage) || 
          component._showToast?.(successMessage);
        },
        onError: () => {
          component.showToast?.('Failed to copy to clipboard', 'error') ||
          component._showToast?.('Failed to copy to clipboard', 'error');
        }
      }
    );
  }
}
